'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  flexRender,
  type ColumnDef,
  type Column,
  type RowData,
  type SortingState,
  type ColumnFiltersState,
  type PaginationState,
  type Updater,
} from '@tanstack/react-table';

import { cn } from '@/hen-ui/utils';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/hen-ui/shadcn/table';
import { Button } from '@/hen-ui/atomics/Button';
import { Checkbox } from '@/hen-ui/atomics/Checkbox';
import { Icon } from '@/hen-ui/atomics/Icon';
import { Input } from '@/hen-ui/atomics/Input';
import { Select, SelectItem } from '@/hen-ui/atomics/Select';

// ---------------------------------------------------------------------------
// TanStack ColumnMeta module augmentation — adds an `align` slot that consumers
// set on any ColumnDef (`meta: { align: 'right' }`) and DataTable honours when
// rendering both the header and body cells. This is the canonical TanStack
// extension pattern; it intentionally keeps DataTable's prop surface unchanged
// so existing callers render identically when they don't opt in.
// ---------------------------------------------------------------------------
declare module '@tanstack/react-table' {
  // The generics are required by the upstream interface signature even though
  // we don't reference them — TS would widen ColumnMeta otherwise.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    align?: 'left' | 'right' | 'center';
  }
}

type ColumnAlign = 'left' | 'right' | 'center';

// Returns undefined for the default ('left' or unset) so cn() short-circuits and
// the shadcn primitive's hardcoded `text-left` is left untouched. tailwind-merge
// (inside cn()) resolves the conflict for 'right' / 'center', so callers don't
// need to worry that `text-left` lexically appears first in the merged class list.
const alignClassFor = (align?: ColumnAlign): string | undefined =>
  align === 'right'
    ? 'text-right'
    : align === 'center'
      ? 'text-center'
      : undefined;

// Header cells wrap their contents in a flex row (so the sort button and filter
// icon sit alongside the label). text-* on the <th> alone won't move flex
// children — we also need to flip the row's main-axis justification.
const alignJustifyFor = (align?: ColumnAlign): string | undefined =>
  align === 'right'
    ? 'justify-end'
    : align === 'center'
      ? 'justify-center'
      : undefined;

export interface DataTableProps<TData, TValue = unknown> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  mode?: 'paginated' | 'scrollable';
  pageSize?: number;
  /**
   * Column ids to include in the search bar. Omit to hide the search input entirely.
   * Search uses OR semantics — a row is kept if any listed column contains the query
   * (case-insensitive substring match). No `filterFn` is needed on the column definitions.
   */
  searchColumns?: string[];
  /**
   * Controlled column filters. When provided, DataTable operates in controlled
   * mode for filtering — the consumer owns filter state and supplies it here.
   * Pair with `onColumnFiltersChange` to receive updates.
   */
  columnFilters?: ColumnFiltersState;
  /** Called when column filter state changes in controlled mode. */
  onColumnFiltersChange?: (filters: ColumnFiltersState) => void;
  /**
   * Accessible name for the search input. Placeholder text is not a substitute
   * for an accessible name — screen readers do not announce placeholder-only
   * inputs consistently. Defaults to `"Search table"`.
   */
  searchLabel?: string;
  /**
   * Accessible name for the table as a whole. When supplied, the root element
   * is exposed as `role="region"` with this label so screen readers can list
   * the table as a landmark (e.g. "Awarded scholarships"). Omit for tables
   * whose surrounding heading already provides context.
   */
  'aria-label'?: string;
  className?: string;
}

// ---------------------------------------------------------------------------
// ColumnFilterDropdown — inline icon in the column header.
// Dropdown renders via React Portal into document.body with position:fixed so
// no parent overflow or table stacking context can clip it.
// ---------------------------------------------------------------------------
function ColumnFilterDropdown<TData>({ column }: { column: Column<TData> }) {
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState({ top: 0, left: 0 });
  // Widened to the union that HEN Button forwards (renders <button> without
  // an href). focus() and getBoundingClientRect() live on the shared HTMLElement
  // supertype, so both branches of the union satisfy the call sites below.
  const triggerRef = React.useRef<HTMLButtonElement | HTMLAnchorElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const closeAndReturnFocus = React.useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  // Raw values are preserved here (numbers stay numbers, strings stay strings)
  // so the consumer's filterFn receives values of the correct type for comparison.
  // Coercing to string here would silently break any filterFn that relies on strict equality.
  // Sort is type-aware: purely numeric facets sort numerically (2 < 100), everything
  // else falls back to localeCompare on the stringified value.
  const uniqueValues = Array.from(column.getFacetedUniqueValues().keys()).sort(
    (a, b) => {
      if (typeof a === 'number' && typeof b === 'number') return a - b;
      return String(a).localeCompare(String(b));
    },
  );

  // column.getFilterValue() has type `unknown` — a consumer mixing a scalar
  // filterFn (e.g. includesString) onto a column that also renders this
  // dropdown would yield a non-array value. Guard before we call .includes.
  const rawFilterValue = column.getFilterValue();
  const filterValue: unknown[] = Array.isArray(rawFilterValue)
    ? rawFilterValue
    : [];
  const isActive = filterValue.length > 0;

  const reposition = React.useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    setPos({ top: rect.bottom + 4, left: rect.left });
  }, []);

  const openDropdown = () => {
    reposition();
    setOpen(true);
  };

  // Dismiss via outside click or Escape. Because the dropdown is portaled to
  // document.body it is at the end of DOM order, so Tab from the trigger would
  // skip past it into the next column header. We explicitly move focus into
  // the panel on open and return it to the trigger on close so keyboard users
  // get a coherent flow.
  //
  // Scrolling used to dismiss the dropdown, but that misfired on any
  // focus-driven `scrollIntoView` the browser emitted during checkbox clicks
  // (intermittent, geometry-dependent). Instead we reposition the dropdown on
  // scroll so it tracks with the trigger. If the trigger scrolls out of view
  // the dropdown goes with it — the user can click outside or press Escape to
  // dismiss.
  React.useEffect(() => {
    if (!open) return;

    const firstInteractive = dropdownRef.current?.querySelector<HTMLElement>(
      '[role="checkbox"], button, [tabindex]:not([tabindex="-1"])',
    );
    firstInteractive?.focus();

    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!(target instanceof Node)) return;
      if (triggerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleScroll = (e: Event) => {
      // Internal list scrolls (the options `<ul>` overflow-y-auto) must not
      // move the dropdown — only external scrolls shift the trigger's viewport
      // position.
      if (dropdownRef.current?.contains(e.target as Node)) return;
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const vw = window.innerWidth || document.documentElement.clientWidth;
      // If the trigger has scrolled completely out of the viewport, the
      // dropdown is orphaned — close it. Partial offsets (e.g. from the
      // browser's focus-driven scrollIntoView nudging a checkbox into view)
      // fall through to reposition, preserving "click multiple checkboxes"
      // UX regardless of viewport geometry.
      const fullyOutside =
        rect.bottom < 0 || rect.top > vh || rect.right < 0 || rect.left > vw;
      if (fullyOutside) {
        setOpen(false);
        return;
      }
      reposition();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeAndReturnFocus();
      }
    };
    // `pointerdown` covers mouse, touch, and pen in one listener and fires
    // before `mousedown` would, avoiding any edge cases where a programmatic
    // `click` (e.g. Radix's internal form-bubble) could be interpreted as an
    // outside click.
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    // Capture phase (3rd arg = true): scroll events don't bubble, so a listener
    // on window would never see scrolls from nested scrollable ancestors
    // (table containers, modal bodies, etc.) during the bubble phase. Capture
    // intercepts them on the way down the tree so the dropdown repositions
    // no matter which ancestor scrolls.
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [open, closeAndReturnFocus, reposition]);

  const toggleValue = (value: unknown) => {
    const next = filterValue.includes(value)
      ? filterValue.filter((v) => v !== value)
      : [...filterValue, value];
    column.setFilterValue(next.length ? next : undefined);
  };

  if (uniqueValues.length === 0) return null;

  return (
    <>
      <Button
        ref={triggerRef}
        variant='iconOnly'
        iconBefore='list-filter'
        onClick={open ? () => setOpen(false) : openDropdown}
        // iconOnly variant owns focus/hover treatment. The isActive override
        // recolors the icon when a filter is applied so the header telegraphs
        // "this column is filtered" at a glance.
        className={cn(isActive && 'text-primary-700')}
        aria-label={`Filter ${column.id}`}
        aria-expanded={open}
        aria-haspopup='dialog'
      />

      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            role='group'
            aria-label={`Filter ${column.id}`}
            style={{ position: 'fixed', top: pos.top, left: pos.left }}
            className='z-popover min-w-44 rounded border border-neutral-200 bg-white shadow-md'
          >
            <ul className='max-h-52 overflow-y-auto p-1'>
              {uniqueValues.map((value) => {
                const label = String(value);
                const checked = filterValue.includes(value);
                return (
                  <li key={`${typeof value}:${label}`}>
                    <Checkbox
                      label={label}
                      checked={checked}
                      onCheckedChange={() => toggleValue(value)}
                      className='cursor-pointer rounded px-2 py-1.5 text-sm hover:bg-neutral-50'
                    />
                  </li>
                );
              })}
            </ul>
            <div className='border-t border-neutral-100 p-1 px-2'>
              <Button
                variant='textOnly'
                size='flush'
                onClick={() => column.setFilterValue(undefined)}
                disabled={!isActive}
                className='w-full justify-start rounded text-xs font-normal text-neutral-500 hover:bg-neutral-100 hover:no-underline hover:text-neutral-700'
              >
                Clear filter
              </Button>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

// ---------------------------------------------------------------------------
// DataTable
// ---------------------------------------------------------------------------

/**
 * Generic data table with sorting, pagination, search, and per-column filtering.
 *
 * Powered by TanStack React Table. Column definitions (`ColumnDef<TData>`) are
 * authored by the consumer — the component is purely a renderer.
 *
 * ## Modes
 * - **`paginated`** (default): renders a subset of rows per page with prev/next navigation.
 * - **`scrollable`**: renders all rows inside a fixed-height scroll container with a sticky header.
 *
 * ## Search Bar
 * Pass `searchColumns` (a `string[]` of column ids) to render a search input above the table.
 * Search uses OR semantics across the listed columns — a row is kept if any listed column
 * contains the query (case-insensitive substring match). Consumers do **not** need to define
 * a `filterFn` on the column definitions for search to work; the component owns the search logic.
 *
 * ## Column Filtering
 * Set `enableColumnFilter: true` and provide a `filterFn` on any `ColumnDef` to show
 * a multi-select dropdown in that column's header. Filtering state is managed internally unless
 * `columnFilters` + `onColumnFiltersChange` props are supplied for controlled mode.
 *
 * ## Accessibility
 * - Sortable headers render as `<button>` inside `<th>`; Tab-reachable, Enter/Space to toggle,
 *   with `aria-sort` on the header cell reflecting current state.
 * - Filter dropdowns are exposed as `role="group"` with an `aria-label`; options are native
 *   checkboxes. Opening moves focus into the popup, Escape returns focus to the trigger.
 * - Search input accepts `searchLabel` (defaults to `"Search table"`) for the accessible name.
 * - Row count region uses `aria-live="polite"` so AT users hear updates after filter/search/page changes.
 * - Optional root `aria-label` names the table as a landmark region.
 *
 * @example
 * ```tsx
 * const columns: ColumnDef<User>[] = [
 *   { accessorKey: 'name', header: 'Name', enableSorting: true },
 *   { accessorKey: 'role', header: 'Role', enableColumnFilter: true, filterFn: exactMatch },
 * ];
 *
 * <DataTable
 *   columns={columns}
 *   data={users}
 *   mode="paginated"
 *   pageSize={10}
 *   searchColumns={['name', 'role']}
 * />
 * ```
 */
export function DataTable<TData, TValue = unknown>({
  columns,
  data,
  mode = 'paginated',
  pageSize = 25,
  searchColumns,
  columnFilters: columnFiltersProp,
  onColumnFiltersChange,
  searchLabel = 'Search table',
  'aria-label': ariaLabel,
  className,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [internalColumnFilters, setInternalColumnFilters] =
    React.useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = React.useState('');
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  });

  const isControlled = columnFiltersProp !== undefined;
  const columnFilters = isControlled
    ? columnFiltersProp
    : internalColumnFilters;

  const handleColumnFiltersChange = (updater: Updater<ColumnFiltersState>) => {
    const next =
      typeof updater === 'function' ? updater(columnFilters) : updater;
    if (isControlled) {
      onColumnFiltersChange?.(next);
    } else {
      setInternalColumnFilters(next);
    }
  };

  React.useEffect(() => {
    setPagination((prev) =>
      prev.pageSize === pageSize ? prev : { ...prev, pageSize },
    );
  }, [pageSize]);

  const table = useReactTable({
    data,
    columns,
    // Column filtering is opt-in. Without this default, TanStack enables
    // column filters on every column and falls back to its built-in `auto`
    // filterFn (inNumberRange for numbers) when no filterFn is defined,
    // causing incorrect range-based filtering on unintended columns.
    defaultColumn: { enableColumnFilter: false },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // Only wire the pagination row model in paginated mode. In scrollable
    // mode we want table.getRowModel() to return all filtered rows so the
    // sticky-header container can scroll through them; forcing pageSize to
    // data.length used to work but meant TanStack still sliced/re-sliced on
    // every data mutation — cheap now, wasteful with large datasets. Omitting
    // the row model altogether is the documented "no pagination" path.
    getPaginationRowModel:
      mode === 'paginated' ? getPaginationRowModel() : undefined,
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    // Search bar: custom global filter scoped to the `searchColumns` prop.
    // TanStack calls this once per column per row and ORs the results — so we
    // answer the per-column question (does THIS column match?) and short-circuit
    // columns outside the search scope. No consumer-side filterFn required.
    globalFilterFn: (row, columnId, value: string) => {
      if (!searchColumns?.includes(columnId)) return false;
      return String(row.getValue(columnId) ?? '')
        .toLowerCase()
        .includes(value.toLowerCase());
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: handleColumnFiltersChange,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    state: { sorting, columnFilters, globalFilter, pagination },
  });

  const filteredRowCount = table.getFilteredRowModel().rows.length;
  const pageCount = table.getPageCount();
  // Collision-free id so multiple DataTables on the same page don't share a
  // label target. Matches the label-htmlFor pattern documented in the Select
  // atomic's stories ("Always provide a label associated with the select via
  // htmlFor/id or use floating variant").
  const pageSelectId = React.useId();

  const tableElement = (
    // aria-label is applied to both the outer landmark region (when
    // ariaLabel is supplied) and the <table> itself so screen readers in
    // table-navigation mode (bypassing the region) still get context.
    // Modern SRs deduplicate identical names on nested elements.
    <Table noWrapper={mode === 'scrollable'} aria-label={ariaLabel}>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const isSorted = header.column.getIsSorted();
              const canSort = header.column.getCanSort();
              const canFilter = header.column.getCanFilter();
              const align = header.column.columnDef.meta?.align;
              return (
                <TableHead
                  key={header.id}
                  aria-sort={
                    canSort
                      ? isSorted === 'asc'
                        ? 'ascending'
                        : isSorted === 'desc'
                          ? 'descending'
                          : 'none'
                      : undefined
                  }
                  className={cn(
                    mode === 'scrollable' && 'sticky top-0 z-10 bg-white',
                    alignClassFor(align),
                  )}
                >
                  <div
                    className={cn(
                      'flex items-center gap-1',
                      alignJustifyFor(align),
                    )}
                  >
                    {canSort ? (
                      <button
                        type='button'
                        onClick={header.column.getToggleSortingHandler()}
                        className='flex items-center gap-1 rounded-sm select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                        <Icon
                          icon={
                            isSorted === 'asc'
                              ? 'arrow-up'
                              : isSorted === 'desc'
                                ? 'arrow-down'
                                : 'arrow-up-down'
                          }
                          className='text-neutral-500'
                          aria-hidden={true}
                        />
                      </button>
                    ) : header.isPlaceholder ? null : (
                      flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )
                    )}
                    {canFilter && (
                      <ColumnFilterDropdown
                        column={header.column as Column<TData>}
                      />
                    )}
                  </div>
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.length > 0 ? (
          table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              className='odd:bg-white even:bg-neutral-50 hover:bg-neutral-100/50'
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  className={alignClassFor(cell.column.columnDef.meta?.align)}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell
              colSpan={table.getVisibleFlatColumns().length || 1}
              className='text-center text-neutral-500'
            >
              No results.
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );

  return (
    <div
      aria-label={ariaLabel}
      role={ariaLabel ? 'region' : undefined}
      className={cn(
        'flex flex-col gap-2',
        mode === 'scrollable' && 'h-full',
        className,
      )}
    >
      {searchColumns && searchColumns.length > 0 && (
        <Input
          placeholder='Search...'
          aria-label={searchLabel}
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
        />
      )}

      <p
        className='text-sm text-neutral-500'
        aria-live='polite'
        aria-atomic='true'
      >
        {filteredRowCount} {filteredRowCount === 1 ? 'row' : 'rows'}
      </p>

      {mode === 'scrollable' ? (
        <div className='flex-1 min-h-0 overflow-y-auto'>{tableElement}</div>
      ) : (
        tableElement
      )}

      {mode === 'paginated' && pageCount > 0 && (
        <div className='flex items-center gap-2'>
          <Button
            variant='grayOutline'
            iconBefore='chevron-left'
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            aria-label='Previous page'
          >
            Prev
          </Button>

          {/* sr-only label: sighted users have visual context from the
              surrounding Prev / Page N / Next controls; screen readers need
              the explicit association because Radix renders the trigger as
              role="combobox" and the inline "Page N" text is the current
              value, not a label. */}
          <label htmlFor={pageSelectId} className='sr-only'>
            Page
          </label>
          <Select
            id={pageSelectId}
            value={String(table.getState().pagination.pageIndex)}
            onValueChange={(value) => table.setPageIndex(Number(value))}
          >
            {Array.from({ length: pageCount }, (_, i) => (
              <SelectItem key={i} value={String(i)}>
                Page {i + 1}
              </SelectItem>
            ))}
          </Select>

          <Button
            variant='grayOutline'
            iconAfter='chevron-right'
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            aria-label='Next page'
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
